import { safeUpdateDoc, authedFetch } from '../utils';
import { useEffect, useRef } from 'react';
import { doc } from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';

export const useFlightTracker = (user, events) => {
  const pollCountRef = useRef(0);
  const lastProcessedRef = useRef(new Set());

  useEffect(() => {
    if (!user || !events || events.length === 0) return;

    const tripsWithFlights = events.filter(e => 
      e.type === 'trip' && 
      ((e.flightIn && e.flightIn.length > 0) || (e.flightOut && e.flightOut.length > 0))
    );

    if (tripsWithFlights.length === 0) return;

    const processFlights = async () => {
      // Visibility check
      if (document.visibilityState !== 'visible') return;
      
      // Session limit check
      if (pollCountRef.current >= 3) return;

      const now = new Date();
      const todayStr = now.toISOString().split('T')[0];
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      for (const trip of tripsWithFlights) {
        const flightsIn = (trip.flightIn || []).filter(f => f.trim() !== '');
        const flightsOut = (trip.flightOut || []).filter(f => f.trim() !== '');
        const allFlights = [...new Set([...flightsIn, ...flightsOut])];

        if (allFlights.length === 0) continue;

        for (const flightIdent of allFlights) {
          const isOut = flightsOut.includes(flightIdent);
          const expectedDateStr = isOut ? trip.endDate : trip.startDate;
          const flightData = trip.flightData?.[flightIdent] || {};
          const lastUpdated = flightData.lastUpdated || 0;
          const timeSinceUpdate = Date.now() - lastUpdated;

          // Polling Rules:
          // 0. Skip if flight is marked as final (arrived/landed)
          if (flightData.isFinal) continue;

          // Stop polling if the expected date was more than 2 days ago
          if (expectedDateStr) {
            const expectedDateObj = new Date(expectedDateStr);
            const timeSinceExpected = now.getTime() - expectedDateObj.getTime();
            if (timeSinceExpected > 2 * 24 * 60 * 60 * 1000) continue;
          }

          // 1. Future Flights (> 2 days out): Check once a day (if lastUpdated is not today)
          // 2. Today/Tomorrow Flights: Check if lastUpdated is > 10 minutes ago
          
          const isTodayOrTomorrow = expectedDateStr === todayStr || expectedDateStr === tomorrowStr;
          
          let shouldPoll = false;
          if (isTodayOrTomorrow) {
            if (timeSinceUpdate > 10 * 60 * 1000) shouldPoll = true;
          } else {
            const lastUpdatedDate = new Date(lastUpdated).toISOString().split('T')[0];
            if (lastUpdatedDate !== todayStr) shouldPoll = true;
          }

          if (shouldPoll && !lastProcessedRef.current.has(`${trip.id}-${flightIdent}`)) {
            if (import.meta.env.DEV) console.log(`[FlightTracker] Polling flight ${flightIdent} for trip ${trip.id}`);
            lastProcessedRef.current.add(`${trip.id}-${flightIdent}`);
            pollCountRef.current += 1;
            
            try {
              const response = await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/fetchFlightStatus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ident: flightIdent })
              });
              
              if (!response.ok) throw new Error('Failed to fetch flight status');
              
              const data = await response.json();
              
              if (data.flights && data.flights.length > 0) {
                // Find the flight that matches our expected date (± 2 days)
                let matchedFlight = null;
                if (expectedDateStr) {
                  const expectedTime = new Date(expectedDateStr).getTime();
                  for (const f of data.flights) {
                    const flightTimeStr = f.estimated_out || f.scheduled_out || f.actual_out;
                    if (!flightTimeStr) continue;
                    
                    const flightTime = new Date(flightTimeStr).getTime();
                    const diffDays = Math.abs(flightTime - expectedTime) / (1000 * 60 * 60 * 24);
                    
                    if (diffDays <= 2) {
                      matchedFlight = f;
                      break;
                    }
                  }
                }

                // Only update if we found a flight that matches the trip's timeframe
                if (matchedFlight) {
                  const latestFlight = matchedFlight;
                  const newDepTime = latestFlight.estimated_out || latestFlight.scheduled_out || latestFlight.actual_out;
                  const newArrTime = latestFlight.estimated_in || latestFlight.scheduled_in || latestFlight.actual_in;
                  const status = latestFlight.status;
                  
                  // Determine if flight is finished to stop polling forever
                  const isFinal = status?.toLowerCase().includes('arrived') || status?.toLowerCase().includes('landed');
                  
                  // Compare with last known departure to trigger "UPDATE:" label
                  const isUpdated = flightData.lastKnownDeparture && flightData.lastKnownDeparture !== newDepTime;
                  
                  const updatedFlightData = {
                    ...(trip.flightData || {}),
                    [flightIdent]: {
                      departureTime: newDepTime || null,
                      arrivalTime: newArrTime || null,
                      scheduledDeparture: latestFlight.scheduled_out || null,
                      scheduledArrival: latestFlight.scheduled_in || null,
                      actualDeparture: latestFlight.actual_out || null,
                      actualArrival: latestFlight.actual_in || null,
                      terminal: latestFlight.terminal_out || null,
                      gate: latestFlight.gate_out || null,
                      arrivalTerminal: latestFlight.terminal_in || null,
                      arrivalGate: latestFlight.arrival_in || null,
                      status: status || null,
                      lastUpdated: Date.now(),
                      lastKnownDeparture: newDepTime || null,
                      isUpdated: isUpdated || flightData.isUpdated || false,
                      isFinal: isFinal || false,
                      origin: latestFlight.origin?.code || null,
                      originCity: latestFlight.origin?.city || null,
                      originLat: latestFlight.origin?.latitude || null,
                      originLng: latestFlight.origin?.longitude || null,
                      destination: latestFlight.destination?.code || null,
                      destinationCity: latestFlight.destination?.city || null,
                      destinationLat: latestFlight.destination?.latitude || null,
                      destinationLng: latestFlight.destination?.longitude || null
                    }
                  };

                  await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'events', trip.id), {
                    flightData: updatedFlightData
                  });
                } else {
                  // Flight not found for this date. We can just update lastUpdated so we don't spam the API.
                  const updatedFlightData = {
                    ...(trip.flightData || {}),
                    [flightIdent]: {
                      ...(trip.flightData?.[flightIdent] || {}),
                      lastUpdated: Date.now()
                    }
                  };
                  await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'events', trip.id), {
                    flightData: updatedFlightData
                  });
                }
              }
            } catch (err) {
              console.error(`[FlightTracker] Error polling ${flightIdent}:`, err);
            }
          }
        }
      }
    };

    // Initial check
    processFlights();

    // Set up 10-minute interval for today/tomorrow flights
    const interval = setInterval(processFlights, 10 * 60 * 1000);

    // Visibility listener
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') processFlights();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, events]);
};
